/**
 * CampaignsPanel — Phase D admin tab.
 *
 * CRUD for campaigns + one-click publish/unpublish + ERIO Phase 0
 * instantiation queue button. Visible when ui_flags.show_campaigns is
 * true (founder + sales_manager per the matrix).
 *
 * Endpoints (admin):
 *   GET    /api/admin/campaigns
 *   POST   /api/admin/campaigns
 *   PATCH  /api/admin/campaigns/{id}
 *   POST   /api/admin/campaigns/{id}/publish
 *   POST   /api/admin/campaigns/{id}/unpublish
 *   DELETE /api/admin/campaigns/{id}
 *   POST   /api/admin/campaigns/{id}/erio-instantiate
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Plus, Globe, EyeOff, Trash2, Edit3, X, ExternalLink,
  Rocket, Copy, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { erioDeepLink } from "../../lib/ssoSession";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const STATUS_STYLES = {
  draft:     "ek-surface-2 ek-text-soft ek-line",
  published: "bg-emerald-50 text-emerald-800 border-emerald-300",
  archived:  "ek-surface-2 ek-text-muted ek-line line-through",
};

function Pill({ text, styles, testid }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10.5px] tracking-[0.12em] uppercase border ${styles}`}
      data-testid={testid}
    >
      {text.replace(/_/g, " ")}
    </span>
  );
}

const SLUG_HINT = "lowercase, hyphen-separated. Becomes /c/your-slug";

function CampaignForm({ initial, onCancel, onSubmit, submitting }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [frameworkId, setFrameworkId] = useState(initial?.framework_id || "framework_pmo360_v09");
  const [heroTitle, setHeroTitle] = useState(initial?.hero_title || "");
  const [heroSubtitle, setHeroSubtitle] = useState(initial?.hero_subtitle || "");
  const [bodyMd, setBodyMd] = useState(initial?.body_md || "");
  const [ctaLabel, setCtaLabel] = useState(initial?.cta_label || "Run the lite assessment");
  const [ctaTarget, setCtaTarget] = useState(initial?.cta_target || "/assessments");

  // Auto-slug from name on create
  const handleNameChange = (v) => {
    setName(v);
    if (!isEdit && !slug) {
      setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60));
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      slug: slug.trim(),
      framework_id: frameworkId.trim(),
      hero_title: heroTitle.trim(),
      hero_subtitle: heroSubtitle.trim(),
      body_md: bodyMd.trim(),
      cta_label: ctaLabel.trim(),
      cta_target: ctaTarget.trim(),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4"
      data-testid="campaign-form-modal"
    >
      <div className="ek-surface max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b ek-line sticky top-0 ek-surface">
          <h3 className="ek-heading text-lg">{isEdit ? `Edit ${initial.name}` : "New campaign"}</h3>
          <button
            type="button"
            onClick={onCancel}
            className="ek-text-muted hover:text-black"
            data-testid="campaign-form-close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Name
            <input
              required
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-name"
              maxLength={160}
            />
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Slug
            <input
              required
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] font-mono focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-slug"
              maxLength={80}
              pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
              title="lowercase, hyphen-separated"
              disabled={isEdit}
            />
            <span className="text-[11px] ek-text-muted normal-case tracking-normal block mt-1">{SLUG_HINT}</span>
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Framework
            <input
              required
              type="text"
              value={frameworkId}
              onChange={(e) => setFrameworkId(e.target.value)}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] font-mono focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-framework"
            />
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Hero title
            <input
              required
              type="text"
              value={heroTitle}
              onChange={(e) => setHeroTitle(e.target.value)}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-hero-title"
              maxLength={200}
            />
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Hero subtitle
            <textarea
              value={heroSubtitle}
              onChange={(e) => setHeroSubtitle(e.target.value)}
              rows={2}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-hero-subtitle"
              maxLength={400}
            />
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Body (plain text or simple markdown)
            <textarea
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              rows={6}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="campaign-form-body"
              maxLength={20000}
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              CTA label
              <input
                required
                type="text"
                value={ctaLabel}
                onChange={(e) => setCtaLabel(e.target.value)}
                className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
                data-testid="campaign-form-cta-label"
                maxLength={80}
              />
            </label>
            <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
              CTA target (URL or path)
              <input
                required
                type="text"
                value={ctaTarget}
                onChange={(e) => setCtaTarget(e.target.value)}
                className="mt-2 w-full border ek-line px-3 py-2 text-[14px] font-mono focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
                data-testid="campaign-form-cta-target"
                maxLength={300}
              />
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t ek-line">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-[12px] tracking-[0.14em] uppercase ek-text-soft hover:text-black"
              data-testid="campaign-form-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase px-5 py-2 disabled:opacity-60"
              data-testid="campaign-form-submit"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CampaignRow({ campaign, onPublish, onUnpublish, onEdit, onDelete, onCopyErio, busyId }) {
  const isBusy = busyId === campaign.id;
  const erioUrl = erioDeepLink(`/?source=ekselens-campaign-${encodeURIComponent(campaign.slug)}`);
  return (
    <tr className="border-t ek-line align-top" data-testid={`campaign-row-${campaign.id}`}>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{campaign.name}</div>
        <div className="ek-text-muted font-mono text-[11px]">/c/{campaign.slug}</div>
        <div className="ek-text-muted text-[11px]">{campaign.framework_id}</div>
      </td>
      <td className="px-3 py-3">
        <Pill
          text={campaign.status}
          styles={STATUS_STYLES[campaign.status] || STATUS_STYLES.draft}
          testid={`campaign-status-${campaign.id}`}
        />
      </td>
      <td className="px-3 py-3 text-[11.5px] ek-text-muted">
        <div>Created {fmtDate(campaign.created_at)}</div>
        {campaign.published_at && <div>Published {fmtDate(campaign.published_at)}</div>}
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-2 justify-end">
          {campaign.public_visible && (
            <a
              href={`/c/${campaign.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
              data-testid={`campaign-view-${campaign.id}`}
            >
              <ExternalLink size={12} /> View
            </a>
          )}
          {campaign.status !== "published" ? (
            <button
              type="button"
              onClick={() => onPublish(campaign)}
              disabled={isBusy}
              className="ek-btn bg-emerald-600 text-white text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid={`campaign-publish-${campaign.id}`}
            >
              <Globe size={12} /> Publish
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onUnpublish(campaign)}
              disabled={isBusy}
              className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black disabled:opacity-60"
              data-testid={`campaign-unpublish-${campaign.id}`}
            >
              <EyeOff size={12} /> Unpublish
            </button>
          )}
          <a
            href={erioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[#7737F6] text-[#7737F6] text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#7737F6] hover:text-white"
            data-testid={`campaign-open-in-erio-${campaign.id}`}
            title="Open this campaign's ERIO workspace (SSO carries the user across)"
          >
            <Rocket size={12} /> Open in ERIO
          </a>
          <button
            type="button"
            onClick={() => onCopyErio(campaign, erioUrl)}
            className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
            data-testid={`campaign-copy-erio-link-${campaign.id}`}
            title="Copy the ERIO deep-link to clipboard"
          >
            <Copy size={12} /> Copy ERIO link
          </button>
          <button
            type="button"
            onClick={() => onEdit(campaign)}
            disabled={isBusy}
            className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black disabled:opacity-60"
            data-testid={`campaign-edit-${campaign.id}`}
          >
            <Edit3 size={12} /> Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete(campaign)}
            disabled={isBusy}
            className="border border-rose-300 text-rose-700 text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:bg-rose-50 disabled:opacity-60"
            data-testid={`campaign-delete-${campaign.id}`}
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function CampaignsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | campaign obj
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/campaigns");
      setItems(r.data?.items || []);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) toast.error("You don't have access to Campaigns.");
      else toast.error("Couldn't load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing && editing !== "new" && editing.id) {
        // PATCH (exclude slug — backend treats it as immutable post-create)
        const { slug: _unused, ...patch } = payload;
        void _unused;
        const r = await api.patch(`/admin/campaigns/${editing.id}`, patch);
        setItems((prev) => prev.map((c) => (c.id === r.data.id ? r.data : c)));
        toast.success("Campaign updated.");
      } else {
        const r = await api.post("/admin/campaigns", payload);
        setItems((prev) => [r.data, ...prev]);
        toast.success("Campaign created.");
      }
      setEditing(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail.message || detail.error) : detail;
      if (detail === "slug_already_in_use") toast.error("That slug is already in use.");
      else toast.error(msg || "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (campaign) => {
    setBusyId(campaign.id);
    try {
      const r = await api.post(`/admin/campaigns/${campaign.id}/publish`);
      setItems((prev) => prev.map((c) => (c.id === r.data.id ? r.data : c)));
      toast.success(`Published — live at /c/${campaign.slug}`);
    } catch {
      toast.error("Publish failed.");
    } finally { setBusyId(null); }
  };

  const handleUnpublish = async (campaign) => {
    setBusyId(campaign.id);
    try {
      const r = await api.post(`/admin/campaigns/${campaign.id}/unpublish`);
      setItems((prev) => prev.map((c) => (c.id === r.data.id ? r.data : c)));
      toast.success("Unpublished.");
    } catch {
      toast.error("Unpublish failed.");
    } finally { setBusyId(null); }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.name}"? This cannot be undone.`)) return;
    setBusyId(campaign.id);
    try {
      await api.delete(`/admin/campaigns/${campaign.id}`);
      setItems((prev) => prev.filter((c) => c.id !== campaign.id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    } finally { setBusyId(null); }
  };

  const handleCopyErio = async (campaign, erioUrl) => {
    try {
      await navigator.clipboard.writeText(erioUrl);
      toast.success("ERIO deep-link copied to clipboard.");
    } catch {
      // Fallback for clipboard-blocked contexts: surface the URL in a toast
      // so the operator can copy it manually.
      toast.message(`ERIO link: ${erioUrl}`);
    }
  };

  const counts = useMemo(() => {
    const c = { draft: 0, published: 0, archived: 0 };
    items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div data-testid="campaigns-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Campaigns</h2>
          <p className="text-[13px] ek-text-muted mt-1 max-w-2xl">
            Create slug-routed landing pages that drop a visitor straight into a
            framework-targeted lite assessment. Publish flips the page live at
            <span className="font-mono"> /c/your-slug</span>; “Open in ERIO” opens a
            deep-link to <span className="font-mono">erio.ekselens.com</span> with the user
            already signed-in via the shared Auth0 tenant.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted" data-testid="campaigns-counts">
            {counts.published} live · {counts.draft} draft
          </div>
          <button
            type="button"
            onClick={load}
            className="ek-btn ek-btn-outline text-[12px] inline-flex items-center gap-2"
            data-testid="campaigns-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase inline-flex items-center gap-2 px-4 py-2"
            data-testid="campaigns-new-btn"
          >
            <Plus size={13} /> New campaign
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border ek-line">
        <table className="min-w-full text-[13px]">
          <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
            <tr>
              <th className="px-3 py-2">Campaign</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Dates</th>
              <th className="px-3 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center ek-text-muted" data-testid="campaigns-loading">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center">
                  <CheckCircle2 size={28} className="mx-auto ek-text-muted" />
                  <div className="ek-heading text-base mt-3" data-testid="campaigns-empty">No campaigns yet</div>
                  <div className="text-[13px] ek-text-muted mt-1 max-w-md mx-auto">
                    Click <strong>New campaign</strong> to generate your first landing page.
                  </div>
                </td>
              </tr>
            )}
            {!loading && items.map((c) => (
              <CampaignRow
                key={c.id}
                campaign={c}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onEdit={(camp) => setEditing(camp)}
                onDelete={handleDelete}
                onCopyErio={handleCopyErio}
                busyId={busyId}
              />
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CampaignForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}

      <div className="mt-6 text-[12px] ek-text-muted flex items-start gap-2 max-w-3xl">
        <AlertCircle size={14} className="mt-0.5" />
        <span>
          &ldquo;Open in ERIO&rdquo; opens <span className="font-mono">erio.ekselens.com</span> with the
          campaign slug appended — the shared Auth0 tenant means the user lands signed-in.
          Any leads ERIO captures flow back here via the unified Leads tab.
        </span>
      </div>
    </div>
  );
}
